import {
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ReportField } from '../../../models/api/reports/report';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { ButtonComponent } from '../../core/buttons/button.component';
import {
  ExportButtonComponent,
  ExportFormat,
} from '../../core/buttons/export-button.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { InputComponent } from '../../core/controls/input.component';
import { SelectComponent } from '../../core/controls/select.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { DialogComponent } from '../../core/panels/dialog.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';

export interface DualListConfig {
  sourcePanelTitle?: string;
  targetPanelTitle?: string;
  sourceFilterLabel?: string;
  targetFilterLabel?: string;
  sourceFilterPlaceholder?: string;
  targetFilterPlaceholder?: string;
  sourceEmptyIcon?: string;
  sourceEmptyLabel?: string;
  sourceEmptyMessage?: string;
  hideSourceEmptyState?: boolean;
  sourceNoResultsLabel?: string;
  sourceNoResultsMessage?: string;
  targetEmptyIcon?: string;
  targetEmptyLabel?: string;
  targetEmptyMessage?: string;
  targetNoResultsLabel?: string;
  targetNoResultsMessage?: string;
  recordLabel?: string;
  disabledMessage?: string;
  disabledIcon?: string;
  showHeaderMessage?: boolean;
  headerMessage?: string;
  enableGroupCollapse?: boolean;
}

export interface DualListButtons {
  transfer?: boolean;
  reorder?: boolean;
}

export interface LevelOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-dual-list-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DragDropModule,
    ScrollingModule,
    InputComponent,
    SelectComponent,
    EmptyStateComponent,
    LoadingPanelComponent,
    IconButtonComponent,
    ButtonComponent,
    ExportButtonComponent,
    DialogComponent,
    ActionRowComponent,
  ],
  template: `
    @if (showLevelDropdown) {
      <div class="level-dropdown-section tw-mb-4">
        <div class="tw-w-[300px]">
          <app-select
            [control]="levelControl"
            [options]="levelOptions"
            optionLabel="label"
            optionValue="value"
            [label]="levelDropdownLabel"
            [showClear]="false"
            [filter]="false"
            [virtualScroll]="false"
          />
        </div>
      </div>
    }
    @if (showHeaderMessage) {
      <div class="tw-flex tw-items-center tw-justify-between tw-pt-4 tw-pb-6">
        <div class="tw-text-[var(--sed-dynamic-black-color)] tw-font-bold">
          {{ headerMessage }}
        </div>
      </div>
    }
    <div class="container" [class.disabled]="disabled" cdkDropListGroup>
      <div>
        <div
          class="tw-font-bold tw-text-[12px] tw-text-[var(--sed-black-80-color)] tw-pb-1 tw-pl-1"
        >
          {{ sourcePanelTitle }}: {{ visibleSourceCount }} items
        </div>
        <div class="panel" [style.width.px]="availableWidth">
          <div class="panel-content">
            <div class="filter-section">
              <app-input
                class="tw-w-full"
                [control]="sourceFilterControl"
                [label]="sourceFilterLabel"
                [placeholder]="sourceFilterPlaceholder"
                [showHelpText]="false"
                [showAudio]="true"
              />
            </div>
            <div class="list-wrapper">
              @if (disabled) {
                <app-empty-state
                  [icon]="disabledIcon"
                  label="Selection Disabled"
                  [message]="disabledMessage"
                  height="500px"
                />
              } @else if (loading) {
                <div class="loading-wrapper">
                  <app-loading-panel
                    height="500px"
                    [loadingStyle]="'Box'"
                    [state]="'Loading'"
                    [loadingHeight]="500"
                  />
                </div>
              } @else if (
                source.length === 0 &&
                !sourceFilterControl.value &&
                !hideSourceEmptyState
              ) {
                <app-empty-state
                  [icon]="sourceEmptyIcon"
                  [label]="sourceEmptyLabel"
                  [message]="sourceEmptyMessage"
                  height="500px"
                />
              } @else if (
                source.length === 0 &&
                sourceFilterControl.value &&
                !hideSourceEmptyState
              ) {
                <app-empty-state
                  icon="fa-light fa-input-text"
                  [label]="sourceNoResultsLabel"
                  [message]="sourceNoResultsMessage"
                  height="500px"
                />
              } @else if (visibleSourceCount === 0 && itemFilter) {
                <app-empty-state
                  icon="fa-light fa-filter-slash"
                  label="No fields available"
                  message="No fields match the current filter criteria"
                  height="500px"
                />
              } @else {
                @if (isTransferring) {
                  <div class="loading-wrapper">
                    <app-loading-panel
                      height="500px"
                      [loadingStyle]="'Box'"
                      [state]="'Loading'"
                      [loadingHeight]="500"
                    />
                  </div>
                } @else {
                  <cdk-virtual-scroll-viewport
                    [itemSize]="32"
                    class="virtual-scroll-container"
                    #sourceViewport
                  >
                    <div
                      class="virtual-list"
                      cdkDropList
                      #sourceDropList="cdkDropList"
                      [cdkDropListData]="source"
                      [cdkDropListConnectedTo]="
                        targetDropList ? [targetDropList] : []
                      "
                      (cdkDropListDropped)="onSourceDrop($event)"
                    >
                      <ng-container
                        *cdkVirtualFor="
                          let row of flattenedSource;
                          trackBy: trackByTrackId
                        "
                      >
                        @if (row.isHeader) {
                          <div
                            class="group-header"
                            [class.collapsible]="enableGroupCollapse"
                            (click)="toggleGroup(row.groupKey)"
                            (keyup.enter)="toggleGroup(row.groupKey)"
                            (keyup.space)="toggleGroup(row.groupKey)"
                            [tabindex]="0"
                          >
                            @if (enableGroupCollapse) {
                              <span
                                class="chevron-icon"
                                [class.collapsed]="
                                  isGroupCollapsed(row.groupKey)
                                "
                              >
                                <i class="fa-light fa-chevron-down"></i>
                              </span>
                            }
                            <span class="group-label">{{ row.label }}</span>
                            <span class="group-badge">{{ row.count }}</span>
                          </div>
                        }
                        @if (!row.isHeader) {
                          <div
                            cdkDrag
                            [cdkDragData]="row.item"
                            class="row"
                            [class.selected]="isSourceSelected(row.item)"
                            [title]="
                              row.item.description ||
                              row.item.defaultValue ||
                              ''
                            "
                            (click)="selectSourceItem(row.item)"
                            (keyup.enter)="selectSourceItem(row.item)"
                            (keyup.space)="selectSourceItem(row.item)"
                            (dblclick)="moveToTarget(row.item)"
                            [tabindex]="0"
                          >
                            <div class="cell-field">
                              {{ row.item.defaultValue }}
                            </div>
                            <div
                              class="drag-placeholder"
                              *cdkDragPlaceholder
                            ></div>
                          </div>
                        }
                      </ng-container>
                    </div>
                  </cdk-virtual-scroll-viewport>
                }
              }
            </div>
          </div>
        </div>
      </div>

      @if (showTransferButtons) {
        <ul class="transfer tw-list-none tw-m-0 tw-p-0">
          <li class="tw-contents desktop-icon">
            <app-icon-button
              icon="fa-light fa-chevrons-right"
              tooltip="Add all to selected"
              (click)="moveAllToTarget()"
              [disabled]="disabled"
            />
          </li>
          <li class="tw-contents mobile-icon">
            <app-icon-button
              icon="fa-light fa-chevrons-down"
              tooltip="Add all to selected"
              (click)="moveAllToTarget()"
              [disabled]="disabled"
            />
          </li>
          <li class="tw-contents desktop-icon">
            <app-icon-button
              icon="fa-light fa-chevron-right"
              tooltip="Add selected to target"
              (click)="moveSingleToTarget()"
              [disabled]="disabled"
            />
          </li>
          <li class="tw-contents mobile-icon">
            <app-icon-button
              icon="fa-light fa-chevron-down"
              tooltip="Add selected to target"
              (click)="moveSingleToTarget()"
              [disabled]="disabled"
            />
          </li>
          <li class="tw-contents desktop-icon">
            <app-icon-button
              icon="fa-light fa-chevron-left"
              tooltip="Remove selected from target"
              (click)="moveSingleToSource()"
              [disabled]="disabled"
            />
          </li>
          <li class="tw-contents mobile-icon">
            <app-icon-button
              icon="fa-light fa-chevron-up"
              tooltip="Remove selected from target"
              (click)="moveSingleToSource()"
              [disabled]="disabled"
            />
          </li>
          <li class="tw-contents desktop-icon">
            <app-icon-button
              icon="fa-light fa-chevrons-left"
              tooltip="Remove all from selected"
              (click)="moveAllToSource()"
              [disabled]="disabled"
            />
          </li>
          <li class="tw-contents mobile-icon">
            <app-icon-button
              icon="fa-light fa-chevrons-up"
              tooltip="Remove all from selected"
              (click)="moveAllToSource()"
              [disabled]="disabled"
            />
          </li>
        </ul>
      }

      <div>
        <div
          class="tw-font-bold tw-text-[12px] tw-text-[var(--sed-black-80-color)] tw-pb-1 tw-pl-1"
        >
          {{ targetPanelTitle }}: {{ targetFiltered.length }} items
        </div>
        <div class="panel tw-relative" [style.width.px]="selectedWidth">
          <div class="panel-content">
            <div class="filter-section">
              <app-input
                class="tw-w-full"
                [control]="targetFilterControl"
                [label]="targetFilterLabel"
                [placeholder]="targetFilterPlaceholder"
                [showHelpText]="false"
                [showAudio]="true"
              />
            </div>
            <div class="list-wrapper">
              @if (disabled) {
                <app-empty-state
                  [icon]="disabledIcon"
                  label="Selection Disabled"
                  [message]="disabledMessage"
                  height="500px"
                />
              } @else if (isTransferring || isTargetFiltering) {
                <div class="loading-wrapper">
                  <app-loading-panel
                    height="500px"
                    [loadingStyle]="'Box'"
                    [state]="'Loading'"
                    [loadingHeight]="500"
                  />
                </div>
              } @else {
                <div
                  class="drop-zone-wrapper"
                  cdkDropList
                  #targetDropList="cdkDropList"
                  [cdkDropListData]="target"
                  [cdkDropListConnectedTo]="
                    sourceDropList ? [sourceDropList] : []
                  "
                  (cdkDropListDropped)="onTargetDrop($event)"
                >
                  @if (
                    targetFiltered.length === 0 && !targetFilterControl.value
                  ) {
                    <app-empty-state
                      [icon]="targetEmptyIcon"
                      [label]="targetEmptyLabel"
                      [message]="targetEmptyMessage"
                      height="500px"
                    />
                  } @else if (
                    targetFiltered.length === 0 && targetFilterControl.value
                  ) {
                    <app-empty-state
                      icon="fa-light fa-search"
                      [label]="targetNoResultsLabel"
                      [message]="targetNoResultsMessage"
                      height="500px"
                    />
                  } @else {
                    <cdk-virtual-scroll-viewport
                      [itemSize]="32"
                      class="virtual-scroll-container"
                      #targetViewport
                    >
                      <div class="virtual-list">
                        <ng-container
                          *cdkVirtualFor="
                            let row of flattenedTarget;
                            trackBy: trackByTrackId
                          "
                        >
                          <div
                            cdkDrag
                            [cdkDragData]="row.item"
                            class="row"
                            [class.selected]="isSelected(row.item)"
                            [title]="
                              row.item.description ||
                              row.item.defaultValue ||
                              ''
                            "
                            (click)="selectTargetItem(row.item)"
                            (keyup.enter)="selectTargetItem(row.item)"
                            (keyup.space)="selectTargetItem(row.item)"
                            (dblclick)="moveToSource(row.item)"
                            [tabindex]="0"
                          >
                            <div class="cell-field">
                              {{ row.item.defaultValue }}
                            </div>
                            <div
                              class="drag-placeholder"
                              *cdkDragPlaceholder
                            ></div>
                          </div>
                        </ng-container>
                      </div>
                    </cdk-virtual-scroll-viewport>
                  }
                </div>
              }
            </div>
          </div>
          @if (maxSelections > 0) {
            <div class="tw-absolute tw--bottom-6 tw-left-1 tw-italic">
              {{ maxSelections }} maximum
            </div>
          }
        </div>
      </div>

      @if (showReorderButtons) {
        <ul class="reorder-buttons tw-list-none tw-m-0 tw-p-0">
          <li class="tw-contents">
            <app-icon-button
              icon="fa-light fa-chevrons-up"
              tooltip="Move to top"
              (click)="moveTop()"
            />
          </li>
          <li class="tw-contents">
            <app-icon-button
              icon="fa-light fa-chevron-up"
              tooltip="Move up"
              (click)="moveUp()"
            />
          </li>
          <li class="tw-contents">
            <app-icon-button
              icon="fa-light fa-chevron-down"
              tooltip="Move down"
              (click)="moveDown()"
            />
          </li>
          <li class="tw-contents">
            <app-icon-button
              icon="fa-light fa-chevrons-down"
              tooltip="Move to bottom"
              (click)="moveBottom()"
            />
          </li>
        </ul>
      }
    </div>

    @if (showRestoreDefaults || showExport) {
      <div class="tw-mt-4 tw-flex tw-items-center tw-gap-4">
        @if (showRestoreDefaults) {
          <app-button
            label="Restore defaults"
            emphasis="Low"
            (click)="onRestoreDefaults()"
          />
        }
        @if (showExport) {
          <app-export-button
            [csv]="true"
            [xlsx]="true"
            [pdf]="false"
            [loading]="exporting"
            (export)="onExportClicked($event)"
          />
        }
      </div>
    }

    <app-dialog #mandatoryFieldDialog label="Notification" width="30rem">
      <ng-template #content>
        <div class="tw-pb-6">
          One or more of the fields you are attempting to move is mandatory and
          may not be removed.
        </div>
        <app-action-row>
          <app-button (click)="confirmMandatoryDialog()" right1 label="OK" />
          @if (isMoveAllPending) {
            <app-button
              (click)="closeMandatoryDialog()"
              emphasis="Low"
              right2
              label="Cancel"
            />
          }
        </app-action-row>
      </ng-template>
    </app-dialog>
  `,
  styles: `
    .container {
      display: flex;
      gap: 1rem;
      width: 100%;
    }
    .container.disabled {
      opacity: 0.7;
      pointer-events: none;
    }
    .container.disabled .panel {
      background: var(--sed-grey-40-color);
    }
    .level-dropdown-section {
      display: flex;
      align-items: center;
    }
    .panel {
      flex: 0 0 450px;
      border: 1px solid var(--sed-grey-60-color);
      border-radius: 0.5rem;
      padding: 0.25rem;
      background: var(--sed-warm-white-color);
    }
    .panel-content {
      display: flex;
      flex-direction: column;
    }
    .filter-section {
      display: flex;
      padding: 0.5rem 0;
    }
    .list-wrapper {
      height: 500px;
      min-height: 500px;
      max-height: 500px;
    }
    .virtual-scroll-container {
      height: 500px;
      width: 100%;
    }
    .virtual-list {
      width: 100%;
    }
    .loading-wrapper {
      height: 500px;
      min-height: 500px;
      max-height: 500px;
      position: relative;
    }
    .drop-zone-wrapper {
      height: 500px;
      min-height: 500px;
      max-height: 500px;
    }
    .table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    .table thead {
      position: sticky;
      top: 0;
      z-index: 1;
      background: var(--sed-warm-white-color);
    }
    .table th {
      padding: 0.25rem 0.5rem;
      text-align: left;
      font-size: 12px;
      font-weight: 600;
      color: var(--sed-black-80-color);
      border-bottom: 1px solid var(--sed-grey-40-color);
    }
    .col-field {
      width: 100%;
    }
    .row {
      cursor: grab;
      border-bottom: 1px solid var(--sed-grey-40-color);
      background: var(--sed-warm-white-color);
      display: flex;
      align-items: center;
      height: 32px;
      box-sizing: border-box;
    }
    .row:hover {
      background: var(--sed-grey-60-color);
    }
    .row.selected {
      background: var(--sed-grey-40-color);
      font-weight: 600;
    }
    .row.selected:hover {
      background: var(--sed-grey-40-color);
    }
    .row:hover .cell-field {
      text-decoration: underline;
    }
    .row.cdk-drag-preview {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      background: var(--sed-warm-white-color);
      border: 1px solid var(--sed-grey-60-color);
      border-radius: 4px;
    }
    .row.cdk-drag-animating {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
    .drag-placeholder {
      background: var(--sed-grey-40-color);
      border: 2px dashed var(--sed-grey-60-color);
      min-height: 32px;
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
    .cdk-drop-list-dragging .row:not(.cdk-drag-placeholder) {
      transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
    }
    .cell-field {
      padding: 0.25rem 0.5rem;
      font-size: 14px;
      font-weight: 400;
      color: var(--sed-engaged-black-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
    }
    .transfer {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      align-self: center;
    }
    .reorder-buttons {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      align-self: center;
    }
    .group-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
      background: var(--sed-grey-20-color);
      border-bottom: 1px solid var(--sed-grey-40-color);
      height: 32px;
      box-sizing: border-box;
    }
    .group-header.collapsible {
      cursor: pointer;
      user-select: none;
    }
    .group-header.collapsible:hover {
      background: var(--sed-grey-40-color);
    }
    .chevron-icon {
      display: inline-flex;
      width: 16px;
      justify-content: center;
      transition: transform 0.2s ease;
    }
    .chevron-icon.collapsed {
      transform: rotate(-90deg);
    }
    .chevron-icon i,
    .chevron-icon svg {
      font-size: 12px;
    }
    .group-label {
      font-size: 13px;
      font-weight: 700;
      color: var(--sed-engaged-black-color);
    }
    .group-badge {
      background: var(--sed-grey-60-color);
      color: var(--sed-engaged-black-color);
      font-size: 11px;
      font-weight: 600;
      padding: 0.125rem 0.5rem;
      border-radius: 10px;
    }
    .mobile-icon {
      display: none;
    }
    /* Tablet (sm to lg): side-by-side lists, flex panels to fit viewport */
    @media (min-width: 640px) and (max-width: 1023px) {
      .container {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        flex-wrap: nowrap;
      }
      .panel {
        flex: 1 1 0;
        min-width: 0;
        width: auto !important;
        max-width: 450px;
      }
    }
    /* Mobile (below sm): stack lists vertically */
    @media (max-width: 639px) {
      .desktop-icon {
        display: none;
      }
      .mobile-icon {
        display: contents;
      }
      .container {
        flex-direction: column;
      }
      .panel {
        flex: 1 1 auto;
        width: 100%;
        max-width: 100%;
      }
      .transfer {
        flex-direction: row;
        align-self: center;
        justify-content: center;
        padding: 0.5rem 0;
      }
      .reorder-buttons {
        flex-direction: row;
        align-self: center;
        justify-content: center;
        padding: 0.5rem 0;
      }
    }
  `,
})
export class DualListPickerComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly CONFIG_DEFAULTS: Required<DualListConfig> = {
    sourcePanelTitle: 'Available',
    targetPanelTitle: 'Selected',
    sourceFilterLabel: 'Filter available items',
    targetFilterLabel: 'Filter selected items',
    sourceFilterPlaceholder: 'Filter',
    targetFilterPlaceholder: 'Filter',
    sourceEmptyIcon: 'fa-light fa-magnifying-glass',
    sourceEmptyLabel: 'Type to search',
    sourceEmptyMessage: 'Enter at least 3 characters to find fields',
    hideSourceEmptyState: false,
    sourceNoResultsLabel: 'No fields',
    sourceNoResultsMessage: 'No fields found. Try a different search term.',
    targetEmptyIcon: 'fa-light fa-inbox',
    targetEmptyLabel: 'No fields selected',
    targetEmptyMessage: 'Click fields from the left to add them here',
    targetNoResultsLabel: 'No fields',
    targetNoResultsMessage: 'No selected fields match your filter',
    recordLabel: 'item',
    disabledMessage: 'Please select a report level to enable field selection',
    disabledIcon: 'fa-light fa-lock',
    showHeaderMessage: false,
    headerMessage:
      'This report has {count} available fields. Use the filter below to search and double click or use the move all button to select fields.',
    enableGroupCollapse: true,
  };

  @Input() source: ReportField[] = [];
  @Input() target: ReportField[] = [];
  @Input() loading = false;
  @Input() maxSelections = 900;
  @Input() showRestoreDefaults = false;
  @Input() showExport = false;
  @Input() exporting = false;
  @Input() disabled = false;
  @Input() showLevelDropdown = false;
  @Input() levelOptions: LevelOption[] = [];
  @Input() selectedLevel = 'N';
  @Input() levelDropdownLabel = 'Report Level';
  @Input() itemFilter: ((item: ReportField) => boolean) | null = null;
  @Input() isMandatoryField: ((item: ReportField) => boolean) | null = null;
  @Input() availableWidth: number;
  @Input() selectedWidth: number;

  @Input() config: DualListConfig = {};
  @Input() showButtons: DualListButtons = { transfer: true, reorder: true };

  @Output() filter = new EventEmitter<string>();
  @Output() targetChange = new EventEmitter<ReportField[]>();
  @Output() restoreDefaults = new EventEmitter<void>();
  @Output() exportClicked = new EventEmitter<ExportFormat>();
  @Output() levelChange = new EventEmitter<string>();

  sourceFilterControl = new FormControl('');
  targetFilterControl = new FormControl('');
  levelControl = new FormControl<string>('N');
  targetFiltered: ReportField[] = [];
  isTransferring = false;
  isTargetFiltering = false;
  selectedSourceItem: ReportField | null = null;
  selectedTargetItem: ReportField | null = null;

  private snackbar = inject(Snackbar);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  @ViewChild('sourceViewport') sourceViewport: CdkVirtualScrollViewport;
  @ViewChild('targetViewport') targetViewport: CdkVirtualScrollViewport;
  @ViewChild('sourceDropList') sourceDropList: CdkDropList;
  @ViewChild('targetDropList') targetDropList: CdkDropList;
  @ViewChild('mandatoryFieldDialog') mandatoryFieldDialog: DialogComponent;

  standardFields: ReportField[] = [];
  clientFields: ReportField[] = [];

  get visibleSourceCount(): number {
    return this.standardFields.length + this.clientFields.length;
  }

  flattenedSource: FlattenedSourceRow[] = [];
  flattenedTarget: FlattenedSourceRow[] = [];

  collapsedGroups = new Set<string>();

  toggleGroup(groupKey: string) {
    if (!this.enableGroupCollapse) {
      return;
    }

    if (!groupKey) return;

    if (this.collapsedGroups.has(groupKey)) {
      this.collapsedGroups.delete(groupKey);
    } else {
      this.collapsedGroups.add(groupKey);
    }
    this.rebuildFlattenedSource();
    this.cdr.markForCheck();
  }

  isGroupCollapsed(groupKey: string): boolean {
    return this.collapsedGroups.has(groupKey);
  }

  private getConfig<K extends keyof DualListConfig>(
    key: K,
  ): NonNullable<DualListConfig[K]> {
    return (this.config[key] ??
      DualListPickerComponent.CONFIG_DEFAULTS[key]) as NonNullable<
      DualListConfig[K]
    >;
  }

  get sourcePanelTitle() {
    return this.getConfig('sourcePanelTitle');
  }
  get targetPanelTitle() {
    return this.getConfig('targetPanelTitle');
  }
  get sourceFilterLabel() {
    return this.getConfig('sourceFilterLabel');
  }
  get targetFilterLabel() {
    return this.getConfig('targetFilterLabel');
  }
  get sourceFilterPlaceholder() {
    return this.getConfig('sourceFilterPlaceholder');
  }
  get targetFilterPlaceholder() {
    return this.getConfig('targetFilterPlaceholder');
  }
  get recordLabel() {
    return this.getConfig('recordLabel');
  }
  get sourceEmptyIcon() {
    return this.getConfig('sourceEmptyIcon');
  }
  get sourceEmptyLabel() {
    return this.getConfig('sourceEmptyLabel');
  }
  get sourceEmptyMessage() {
    return this.getConfig('sourceEmptyMessage');
  }
  get hideSourceEmptyState() {
    return this.getConfig('hideSourceEmptyState');
  }
  get sourceNoResultsLabel() {
    return this.getConfig('sourceNoResultsLabel');
  }
  get sourceNoResultsMessage() {
    return this.getConfig('sourceNoResultsMessage');
  }
  get targetEmptyIcon() {
    return this.getConfig('targetEmptyIcon');
  }
  get targetEmptyLabel() {
    return this.getConfig('targetEmptyLabel');
  }
  get targetEmptyMessage() {
    return this.getConfig('targetEmptyMessage');
  }
  get targetNoResultsLabel() {
    return this.getConfig('targetNoResultsLabel');
  }
  get targetNoResultsMessage() {
    return this.getConfig('targetNoResultsMessage');
  }
  get disabledMessage() {
    return this.getConfig('disabledMessage');
  }
  get disabledIcon() {
    return this.getConfig('disabledIcon');
  }
  get showHeaderMessage() {
    return this.getConfig('showHeaderMessage');
  }
  get enableGroupCollapse() {
    return this.getConfig('enableGroupCollapse');
  }
  get headerMessage() {
    return this.getConfig('headerMessage').replace(
      '{count}',
      String(this.maxSelections),
    );
  }
  get showTransferButtons() {
    return this.showButtons.transfer ?? true;
  }
  get showReorderButtons() {
    return this.showButtons.reorder ?? true;
  }

  ngOnInit() {
    this.sourceFilterControl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => this.filter.emit(value || ''));

    this.targetFilterControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyTargetFilter(true));

    this.levelControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value !== null) {
          this.levelChange.emit(value);
        }
      });

    if (this.selectedLevel) {
      this.levelControl.setValue(this.selectedLevel, { emitEvent: false });
    }

    this.applyTargetFilter();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedLevel'] && !changes['selectedLevel'].firstChange) {
      const newLevel = changes['selectedLevel'].currentValue;
      this.levelControl.setValue(newLevel, { emitEvent: false });
    }

    this.updateGroupedFields();
    this.applyTargetFilter();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateGroupedFields(): void {
    const standard: ReportField[] = [];
    const client: ReportField[] = [];

    for (const field of this.source) {
      if (this.itemFilter && !this.itemFilter(field)) {
        continue;
      }

      if (field.isClientField) {
        client.push(field);
      } else {
        standard.push(field);
      }
    }

    const compareFn = (a: ReportField, b: ReportField) =>
      (a.defaultValue || '').localeCompare(b.defaultValue || '');

    standard.sort(compareFn);
    client.sort(compareFn);

    this.standardFields = standard;
    this.clientFields = client;

    this.rebuildFlattenedSource();
  }

  private buildFlattenedList(
    standardFields: ReportField[],
    clientFields: ReportField[],
    collapsedGroups: Set<string>,
    prefix = '',
  ): FlattenedSourceRow[] {
    const standardCollapsed = collapsedGroups.has('standard');
    const clientCollapsed = collapsedGroups.has('client');
    const standardItemCount = standardCollapsed ? 0 : standardFields.length;
    const clientItemCount = clientCollapsed ? 0 : clientFields.length;
    const headerCount =
      (standardFields.length > 0 ? 1 : 0) + (clientFields.length > 0 ? 1 : 0);

    const result: FlattenedSourceRow[] = new Array(
      headerCount + standardItemCount + clientItemCount,
    );
    let idx = 0;

    if (standardFields.length > 0) {
      result[idx++] = {
        isHeader: true,
        label: 'Standard fields',
        count: standardFields.length,
        trackId: `${prefix}header-standard-${
          standardCollapsed ? 'collapsed' : 'expanded'
        }`,
        groupKey: 'standard',
        isCollapsed: standardCollapsed,
      };
      if (!standardCollapsed) {
        for (const item of standardFields) {
          result[idx++] = {
            isHeader: false,
            item,
            trackId: `${prefix}item-${item.tableAlias}-${item.columnName}`,
            groupKey: 'standard',
          };
        }
      }
    }

    if (clientFields.length > 0) {
      result[idx++] = {
        isHeader: true,
        label: 'Client fields',
        count: clientFields.length,
        trackId: `${prefix}header-client-${
          clientCollapsed ? 'collapsed' : 'expanded'
        }`,
        groupKey: 'client',
        isCollapsed: clientCollapsed,
      };
      if (!clientCollapsed) {
        for (const item of clientFields) {
          result[idx++] = {
            isHeader: false,
            item,
            trackId: `${prefix}item-${item.tableAlias}-${item.columnName}`,
            groupKey: 'client',
          };
        }
      }
    }

    return result;
  }

  private rebuildFlattenedSource(): void {
    this.flattenedSource = this.buildFlattenedList(
      this.standardFields,
      this.clientFields,
      this.collapsedGroups,
      '',
    );
  }

  trackByTrackId = (index: number, row: FlattenedSourceRow): string => {
    return row.trackId;
  };

  trackByFieldFn = (index: number, item: ReportField): string => {
    return `${item.tableAlias}-${item.columnName}`;
  };

  applyTargetFilter(showLoading = false) {
    if (showLoading) {
      this.isTargetFiltering = true;
      this.cdr.markForCheck();

      setTimeout(() => {
        this.executeTargetFilter();
        this.isTargetFiltering = false;
        this.cdr.markForCheck();
      }, 0);
    } else {
      this.executeTargetFilter();
    }
  }

  private executeTargetFilter() {
    const filterValue = (this.targetFilterControl.value || '').toLowerCase();
    this.targetFiltered = filterValue
      ? this.target.filter((item) =>
          item.defaultValue?.toLowerCase().includes(filterValue),
        )
      : [...this.target];

    this.rebuildFlattenedTarget();
    this.cdr.markForCheck();
  }

  private rebuildFlattenedTarget(): void {
    this.flattenedTarget = this.targetFiltered.map((item) => ({
      isHeader: false,
      item,
      trackId: `target-item-${item.tableAlias}-${item.columnName}`,
      groupKey: (item.isClientField ? 'client' : 'standard') as
        | 'standard'
        | 'client',
    }));
  }

  selectTargetItem(item: ReportField) {
    this.selectedTargetItem = item;
    this.cdr.markForCheck();
  }

  private isSameField(a: ReportField | null, b: ReportField): boolean {
    return (
      a !== null &&
      a.tableAlias === b.tableAlias &&
      a.columnName === b.columnName
    );
  }

  private findFieldIndex(list: ReportField[], field: ReportField): number {
    return list.findIndex(
      (f) =>
        f.tableAlias === field.tableAlias && f.columnName === field.columnName,
    );
  }

  private emitAndRefresh(): void {
    this.targetChange.emit(this.target);
    this.applyTargetFilter();
    this.cdr.markForCheck();
  }

  isSelected(item: ReportField): boolean {
    return this.isSameField(this.selectedTargetItem, item);
  }

  selectSourceItem(item: ReportField) {
    this.selectedSourceItem = item;
    this.cdr.markForCheck();
  }

  isSourceSelected(item: ReportField): boolean {
    return this.isSameField(this.selectedSourceItem, item);
  }

  trackByField(index: number, item: ReportField): string {
    return `${item.tableAlias}_${item.columnName}`;
  }

  moveToTarget(item: ReportField) {
    if (this.target.length >= this.maxSelections) {
      this.snackbar.show(
        `Maximum ${this.maxSelections} fields allowed`,
        'Info',
      );
      return;
    }
    const index = this.findFieldIndex(this.source, item);
    if (index > -1) {
      this.source.splice(index, 1);
      this.target.push(item);
      this.selectedSourceItem = null;
      this.updateGroupedFields();
      this.emitAndRefresh();
    }
  }

  moveSingleToTarget() {
    if (!this.selectedSourceItem) {
      this.snackbar.show('Please select a field', 'Info');
      return;
    }
    this.moveToTarget(this.selectedSourceItem);
  }

  moveSingleToSource() {
    if (!this.selectedTargetItem) {
      this.snackbar.show('Please select a field', 'Info');
      return;
    }
    this.moveToSource(this.selectedTargetItem);
  }

  moveToSource(item: ReportField) {
    if (this.isMandatoryField?.(item)) {
      this.showMandatoryFieldDialog();
      return;
    }
    const index = this.findFieldIndex(this.target, item);
    if (index > -1) {
      this.target.splice(index, 1);
      this.source.push(item);
      this.selectedTargetItem = null;
      this.updateGroupedFields();
      this.emitAndRefresh();
    }
  }

  moveAllToTarget() {
    const spaceAvailable = this.maxSelections - this.target.length;
    if (spaceAvailable <= 0) {
      this.snackbar.show(
        `Maximum ${this.maxSelections} fields allowed`,
        'Info',
      );
      return;
    }

    this.targetFilterControl.setValue('');
    this.sourceFilterControl.setValue('', { emitEvent: false });
    this.selectedSourceItem = null;
    this.isTransferring = true;
    this.cdr.markForCheck();

    setTimeout(() => {
      let itemsToMove: ReportField[];
      if (this.itemFilter) {
        itemsToMove = this.source
          .filter((f) => this.itemFilter(f))
          .slice(0, spaceAvailable);
        const moveSet = new Set(itemsToMove);
        for (let i = this.source.length - 1; i >= 0; i--) {
          if (moveSet.has(this.source[i])) {
            this.source.splice(i, 1);
          }
        }
      } else {
        itemsToMove = this.source.splice(
          0,
          Math.min(this.source.length, spaceAvailable),
        );
      }
      this.target.push(...itemsToMove);
      this.updateGroupedFields();
      this.applyTargetFilter();
      this.targetChange.emit(this.target);
      this.isTransferring = false;
      this.cdr.markForCheck();
    }, 0);
  }

  private pendingRemovableFields: ReportField[] = [];
  private pendingMandatoryFields: ReportField[] = [];
  isMoveAllPending = false;

  moveAllToSource() {
    const mandatoryFields: ReportField[] = [];
    const removableFields: ReportField[] = [];

    for (const field of this.target) {
      if (this.isMandatoryField && this.isMandatoryField(field)) {
        mandatoryFields.push(field);
      } else {
        removableFields.push(field);
      }
    }

    if (mandatoryFields.length > 0) {
      this.pendingRemovableFields = removableFields;
      this.pendingMandatoryFields = mandatoryFields;
      this.isMoveAllPending = true;
      this.showMandatoryFieldDialog();
      return;
    }

    this.executeMoveAllToSource(removableFields, []);
  }

  private executeMoveAllToSource(
    removableFields: ReportField[],
    mandatoryFields: ReportField[],
  ) {
    this.targetFilterControl.setValue('');
    this.sourceFilterControl.setValue('', { emitEvent: false });
    this.selectedSourceItem = null;
    this.selectedTargetItem = null;
    this.isTransferring = true;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.source.push(...removableFields);
      this.target.length = 0;
      this.target.push(...mandatoryFields);

      this.updateGroupedFields();
      this.applyTargetFilter();
      this.targetChange.emit(this.target);
      this.isTransferring = false;
      this.cdr.markForCheck();
    }, 0);
  }

  moveTop() {
    if (!this.selectedTargetItem) {
      this.snackbar.show('Please select a field', 'Info');
      return;
    }
    const idx = this.findFieldIndex(this.target, this.selectedTargetItem);
    if (idx > 0) {
      this.target.splice(idx, 1);
      this.target.unshift(this.selectedTargetItem);
      this.emitAndRefresh();
    }
  }

  moveUp() {
    if (!this.selectedTargetItem) {
      this.snackbar.show('Please select a field', 'Info');
      return;
    }
    const idx = this.findFieldIndex(this.target, this.selectedTargetItem);
    if (idx > 0) {
      [this.target[idx - 1], this.target[idx]] = [
        this.target[idx],
        this.target[idx - 1],
      ];
      this.emitAndRefresh();
    }
  }

  moveDown() {
    if (!this.selectedTargetItem) {
      this.snackbar.show('Please select a field', 'Info');
      return;
    }
    const idx = this.findFieldIndex(this.target, this.selectedTargetItem);
    if (idx >= 0 && idx < this.target.length - 1) {
      [this.target[idx], this.target[idx + 1]] = [
        this.target[idx + 1],
        this.target[idx],
      ];
      this.emitAndRefresh();
    }
  }

  moveBottom() {
    if (!this.selectedTargetItem) {
      this.snackbar.show('Please select a field', 'Info');
      return;
    }
    const idx = this.findFieldIndex(this.target, this.selectedTargetItem);
    if (idx >= 0 && idx < this.target.length - 1) {
      this.target.splice(idx, 1);
      this.target.push(this.selectedTargetItem);
      this.emitAndRefresh();
    }
  }

  onSourceDrop(event: CdkDragDrop<ReportField[]>) {
    if (event.previousContainer === event.container) {
      return;
    } else {
      const item = event.item.data as ReportField;
      this.moveToSource(item);
    }
  }

  onTargetDrop(event: CdkDragDrop<ReportField[]>) {
    if (event.previousContainer === event.container) {
      const item = event.item.data as ReportField;
      const fromIndex = this.findFieldIndex(this.target, item);
      let toIndex = event.currentIndex;

      if (this.targetFilterControl.value) {
        const dropTarget = this.targetFiltered[event.currentIndex];
        if (dropTarget) {
          toIndex = this.findFieldIndex(this.target, dropTarget);
        }
      }

      if (fromIndex !== -1 && fromIndex !== toIndex) {
        moveItemInArray(this.target, fromIndex, toIndex);
        this.emitAndRefresh();
      }
    } else {
      const item = event.item.data as ReportField;
      if (this.target.length >= this.maxSelections) {
        this.snackbar.show(
          `Maximum ${this.maxSelections} fields allowed`,
          'Info',
        );
        return;
      }
      const sourceIndex = this.findFieldIndex(this.source, item);
      if (sourceIndex > -1) {
        this.source.splice(sourceIndex, 1);
        let insertIndex = event.currentIndex;
        if (this.targetFilterControl.value) {
          const dropTarget = this.targetFiltered[event.currentIndex];
          if (dropTarget) {
            insertIndex = this.findFieldIndex(this.target, dropTarget);
          }
        }
        this.target.splice(insertIndex, 0, item);
        this.selectedSourceItem = null;
        this.updateGroupedFields();
        this.emitAndRefresh();
      }
    }
  }

  onRestoreDefaults() {
    this.sourceFilterControl.setValue('', { emitEvent: false });
    this.targetFilterControl.setValue('', { emitEvent: false });
    this.restoreDefaults.emit();
  }

  onExportClicked(format: ExportFormat) {
    this.exportClicked.emit(format);
  }

  showMandatoryFieldDialog() {
    this.mandatoryFieldDialog.visible = true;
  }

  confirmMandatoryDialog() {
    this.mandatoryFieldDialog.visible = false;
    if (this.isMoveAllPending) {
      this.executeMoveAllToSource(
        this.pendingRemovableFields,
        this.pendingMandatoryFields,
      );
    }
    this.pendingRemovableFields = [];
    this.pendingMandatoryFields = [];
    this.isMoveAllPending = false;
  }

  closeMandatoryDialog() {
    this.mandatoryFieldDialog.visible = false;
    this.pendingRemovableFields = [];
    this.pendingMandatoryFields = [];
    this.isMoveAllPending = false;
  }
}

export interface FlattenedSourceRow {
  isHeader: boolean;
  label?: string;
  count?: number;
  item?: ReportField;
  trackId: string;
  groupKey?: 'standard' | 'client';
  isCollapsed?: boolean;
}
